import AnnotationResolver from 'resolvers/RTMResolvers/AnnotationResolver';
import AccountResolver from 'resolvers/RTMResolvers/AccountResolver';

import CoursewareTargetService from 'pluginServices/services/CoursewareTargetService';

import {
  validateMotivation,
  createAnnotationBodyString,
  createAnnotationTargetString,
  validateBodyString,
  clientAnnotationToMercuryAnnotation,
  mercuryAnnotationToClientAnnotation,
} from './helpers';
import { IDENTIFYING, REPLYING, COMMENTING, IS_NEW_COMMENT } from './constants';
import AnnotationsCache from './AnnotationsByCoursewareCache';
import { COMMENT_PUBLISH_EVENTS } from 'services/AnnotationService/constants';

export default (() => {
  /**
   * @type {AnnotationResolver}
   */
  let annotationResolver;

  /**
   * @type {AccountResolver}
   */
  let accountResolver;

  let isRegistered = false;
  let _annotationsCache = {};
  let subscribers = {};

  /**
   * Fetch a list of annotations for an element
   * @param {string} workspaceId
   * @param {string} workspaceId
   * @param {string} workspaceId
   * @returns {Promise.<[CoursewareAnnotationDto]>} [ProjectDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/courseware-annotation-dto/)
   */
  const authorAnnotationList = async ({
    rootElementId,
    elementId,
    elementType,
    motivation,
  }) => {
    const annotations = await annotationResolver.authorAnnotationList({
      rootElementId,
      elementId,
      elementType,
      motivation,
    });
    if (annotations.length && motivation !== IDENTIFYING) {
      const _annotationsWithAccountList = await getAnnotationsWithAccountList({ annotations });
      return _annotationsWithAccountList;
    }

    return annotations;
  };

  const getAnnotationsWithAccountList = async ({
    annotations,
  }) => {
    const accountIds = annotations.map((value) => value.creatorAccountId);

    const accounts = await accountResolver.authorAccountList({ accountIds });

    if (accounts.length) {
      return annotations.map((annotation) => {
        const _annotation = { ...annotation };

        const { familyName, givenName }
        = accounts.find((account) => account.accountId === annotation.creatorAccountId);

        _annotation.familyName = familyName;
        _annotation.givenName = givenName;

        return _annotation;
      });
    }
    return annotations;
  };

  const authorAnnotationGet = async ({
    annotationId,
    elementId,
    elementType,
  }) => {
    const annotations = await annotationResolver.authorAnnotationGet({
      annotationId,
      elementId,
      elementType,
    });
    return annotations;
  };

  const authorAnnotationCreate = async ({
    rootElementId,
    elementId,
    elementType,
    motivation,
    body,
    target,
  }) => {
    const annotation = await annotationResolver.authorAnnotationCreate({
      rootElementId,
      elementId,
      elementType,
      motivation,
      body,
      target,
    });

    if (annotation) {
      const annotations = [];
      annotations.push(annotation);
      const _annotationsWithAccountList = await getAnnotationsWithAccountList({ annotations });
      return _annotationsWithAccountList[0];
    }

    return {};
  };

  const authorAnnotationUpdate = async ({
    annotationId,
    elementId,
    elementType,
    body,
    target,
  }) => {
    const annotation = await annotationResolver.authorAnnotationUpdate({
      annotationId,
      elementId,
      elementType,
      body,
      target,
    });

    if (annotation) {
      const annotations = [];
      annotations.push(annotation);
      publishEvent(COMMENT_PUBLISH_EVENTS.create);
      const _annotationsWithAccountList = await getAnnotationsWithAccountList({ annotations });
      return _annotationsWithAccountList[0];
    }

    return {};
  };

  const authorAnnotationAggregate = async ({
    rootElementId,
    elementId,
  }) => {
    const annotations = await annotationResolver.authorAnnotationAggregate({
      rootElementId,
      elementId,
    });
    return annotations;
  };

  const authorAnnotationResolve = async ({
    rootElementId,
    coursewareAnnotationKeys,
    resolved,
  }) => {
    const annotations = await annotationResolver.authorAnnotationResolve({
      rootElementId,
      coursewareAnnotationKeys,
      resolved,
    });
    publishEvent(COMMENT_PUBLISH_EVENTS.resolved);
    for(const key of coursewareAnnotationKeys){
      const annotation = _findAnnotation(key.id);
      _annotationsCache[annotation?.motivation]?.saveAnnotation({...annotation, resolved });
    }
    return annotations;
  };

  const authorAnnotationRead = async ({
    rootElementId,
    elementId,
    annotationIds,
    elementType,
    read,
  }) =>{
    const annotations = await annotationResolver.authorAnnotationRead({
      rootElementId,
      elementId,
      annotationIds,
      elementType,
      read,
    });
    publishEvent(COMMENT_PUBLISH_EVENTS.read);

    for(const id of annotationIds){
      const annotation = _findAnnotation(id);
      _annotationsCache[annotation?.motivation]?.saveAnnotation({...annotation, read});
    }

    return annotations;
  };

  const authorAnnotationDelete = async ({
    annotationId,
    elementId,
    elementType,
  }) => {
    const annotations = await annotationResolver.authorAnnotationDelete({
      annotationId,
      elementId,
      elementType,
    });
    publishEvent(COMMENT_PUBLISH_EVENTS.delete);

    notifyDelete(annotationId);

    return annotations;
  };

  const authorAnnotationsDelete = async (annotations) => {
    const response = await annotationResolver.authorAnnotationsDelete(annotations);
    publishEvent(COMMENT_PUBLISH_EVENTS.delete);
    for (const annotationId of annotations.annotationIds) {
      notifyDelete(annotationId);
    }
    return response;
  };

  const authorAnnotationsRestore = async (annotations) => {
    const response = await annotationResolver.authorAnnotationsRestore(annotations);
    for (const annotationId of annotations.annotationIds) {
      notifyUpdate(annotationId, annotations.elementId, annotations.elementType);
    }
    return response;
  };

  const createCache = (motivation) => {
    if (!_annotationsCache[motivation]) {
      _annotationsCache[motivation] = new AnnotationsCache(motivation);
    }
  };
  const initializeMotivationCache = async (motivation, target, elementId = null) => {
    createCache(motivation);

    const rootElementId = target.ancestry.length
      ? target.ancestry.slice(-1)[0].elementId
      : target.id;

    // If elementId is null and motivation is COMMENTING or REPLYING, only proceed if cache is completely empty (not initialized)
    if (!elementId && (motivation === COMMENTING || motivation === REPLYING)) {
      if (_annotationsCache[motivation].isInitialized()) {
        return;
      }
    }

    const cacheKey = elementId || rootElementId;
    const isAlreadyFetched = _annotationsCache[motivation].coursewareIDs.includes(cacheKey);

    // First time initialization
    if (!_annotationsCache[motivation].isInitialized() && !_annotationsCache[motivation].isInitializing()) {
      //  Only fetch if elementId is provided OR cache is empty
      if (elementId || !_annotationsCache[motivation].isInitialized()) {
        await _fetchAnnotationsByMotivation(motivation, target, elementId);
      }
      return;
    }

    // Lesson changed and this lesson hasn't been fetched yet
    if (_annotationsCache[motivation].isInitialized() &&
        !_annotationsCache[motivation].isInitializing() &&
        !isAlreadyFetched &&
        elementId) { //  MUST have elementId
      await _fetchAnnotationsByMotivation(motivation, target, elementId);
    }
  };

  const _fetchAnnotationsByMotivation = async (motivation, target, elementId = null) => {
    createCache(motivation);

    _annotationsCache[motivation].setInitializing(true);
    const rootElementId = target.ancestry.length ? target.ancestry.slice(-1)[0].elementId : target.id;
    const cacheKey = elementId || rootElementId;
    _annotationsCache[motivation].addCourwareID(cacheKey);

    const mercuryAnnotations = await authorAnnotationList({
      motivation,
      rootElementId,
      elementId,
      elementType: 'ACTIVITY',
    });

    const richAncestry = elementId
      ? [{ elementId }, ...target.ancestry]
      : target.ancestry;
    const clientAnnotations = await Promise.all(mercuryAnnotations.map(
      (annotation) => mercuryAnnotationToClientAnnotation(annotation, richAncestry)
    ));

    _annotationsCache[motivation].saveAnnotations(clientAnnotations);
    _annotationsCache[motivation].setInitializing(false);
  };

  const _fetchAnnotationById = async (annotationId, target) => {
    let elementId = target.id;
    let elementType = target.type;

    if (target.ancestry.length) {
      const rootElement = target.ancestry.slice(-1)[0];

      elementId = rootElement.elementId;
      elementType = rootElement.elementType;
    }

    const mercuryAnnotation = await authorAnnotationGet({
      annotationId,
      elementId,
      elementType,
    });

    const annotation = await mercuryAnnotationToClientAnnotation(mercuryAnnotation, target.ancestry);
    _annotationsCache[annotation.motivation].addCourwareID(elementId);
    _annotationsCache[annotation.motivation].saveAnnotation(annotation);

    return annotation;
  };

  const getAnnotationsByMotivation = async (motivation, target, elementId = null) => {
    validateMotivation(motivation);
    await initializeMotivationCache(motivation, target, elementId);
    const lookupId = elementId || target.id;
    const annotations = _annotationsCache[motivation].getAnnotationsByCoursewareId(lookupId);
    return annotations;
  };

  const getAnnotationById = async (motivation, annotationId, target) => {
    createCache(motivation);

    let annotation;
    if (_annotationsCache[motivation].isInitialized()) {
      annotation = _annotationsCache[motivation].getAnnotation(annotationId);
    }

    return annotation || (await _fetchAnnotationById(annotationId, target));
  };

  const subscribeToAnnotationMotivation = async (motivation, target, callback, elementId = null) => {
    validateMotivation(motivation);
    await initializeMotivationCache(motivation, target, elementId);
    const subscribeId = elementId || target.id;
    return _annotationsCache[motivation].subscribe(subscribeId, callback);
  };

  const notifyUpdate = async (annotationId, elementId, elementType) => {
    const annotation = await authorAnnotationGet({
      annotationId,
      elementId,
      elementType,
    });

    const { ancestry } = await CoursewareTargetService.build().createTarget(elementId);

    const annotationClient = await mercuryAnnotationToClientAnnotation(annotation, ancestry);
    const mercuryAnnotations = await authorAnnotationList({
      motivation: annotationClient.motivation,
      elementId: annotationClient.target[0].id,
      rootElementId: annotationClient.target[0].ancestry.slice(-1)[0].elementId,
      elementType: annotationClient.target[0].type,
    });

    const clientAnnotations = await Promise.all(mercuryAnnotations.map(
      (mercuryAnnotation) => mercuryAnnotationToClientAnnotation(mercuryAnnotation, ancestry)
    ));
    _annotationsCache[annotation.motivation]?.saveAnnotations(clientAnnotations);
  };

  const notifyDelete = async (annotationId) => {
    const deletedAnnotation = _findAnnotation(annotationId);
    _annotationsCache[deletedAnnotation?.motivation]?.deleteAnnotation(annotationId);
  };

  /**
   * Create annotation in mercury
   */
  const createAnnotation = async (motivation, body, target, subTarget) => {
    const { ancestry } = target;
    const rootElementId = ancestry.slice(-1)[0].elementId;
    validateMotivation(motivation);
    initializeMotivationCache(motivation, target);
    const bodyString = createAnnotationBodyString(body);
    validateBodyString(bodyString);
    const targetString = createAnnotationTargetString(target, subTarget);
    // if the comment body is empty do not make an entry in the backend
    if(motivation === COMMENTING && body[0].value == ''){
      const newCommentObj = {
        isNewComment: true, 
        id: 'new-comment',
        motivation: COMMENTING,
        rootElementId: rootElementId,
        elementId: target.id,
        target: targetString,
        body: bodyString,
      };
      const clientAnnotation = await mercuryAnnotationToClientAnnotation(newCommentObj, ancestry);
      _annotationsCache[clientAnnotation.motivation].saveAnnotation(clientAnnotation);
      return newCommentObj;
    }

    const annotation = await authorAnnotationCreate({
      motivation,
      body: bodyString,
      rootElementId,
      target: targetString,
      elementId: target.id,
      elementType: target.type,
    });

    const clientAnnotation = await mercuryAnnotationToClientAnnotation(annotation, ancestry);
    _annotationsCache[clientAnnotation.motivation].saveAnnotation(clientAnnotation);
    return annotation;
  };

  // The find method returns the correct cache (by motivation) the annotation is stored inside.
  // The getAnnotation grabs that particular annotation from the said cache.
  const _findAnnotation = (annotationId) =>
    Object.values(_annotationsCache)
      .find((cache) => cache.getAnnotation(annotationId))
      ?.getAnnotation(annotationId);

  const setAnnotationBody = async (annotationId, body, target) => {
    const annotation = _findAnnotation(annotationId);

    if (!annotation) {
      throw Error(`Couldn't find annotation ${annotationId} in local cache`);
    }

    const mercuryAnnotationFormat = clientAnnotationToMercuryAnnotation({
      ...annotation,
      body,
    });

    const mercuryAnnotation = await authorAnnotationUpdate({
      body: mercuryAnnotationFormat.body,
      target: mercuryAnnotationFormat.target,
      annotationId: annotation.id,
      elementId: target.id,
      elementType: target.type,
    });

    const clientAnnotation = await mercuryAnnotationToClientAnnotation(mercuryAnnotation, target.ancestry);
    _annotationsCache[clientAnnotation.motivation].saveAnnotation(clientAnnotation);
  };

  const resolveAnnotation = async ({ annotationId, annotationVersion, annnotations, target }) => {
    const { ancestry } = target;
    const rootElementId = ancestry.slice(-1)[0].elementId;
    const RESOLVED = true;

    const coursewareAnnotationKeys = (annotationId && annotationVersion) ?
      [{ id: annotationId, version: annotationVersion }] : annnotations;

    await authorAnnotationResolve({
      rootElementId,
      coursewareAnnotationKeys,
      resolved: RESOLVED,
    });
  };

  const readAnnotation = async (annotationIds, target) =>{
    const { ancestry } = target;
    const rootElementId = ancestry.slice(-1)[0].elementId;
    const READ = true;

    await authorAnnotationRead({
      rootElementId,
      annotationIds,
      elementType: target.type,
      elementId: target.id,
      read: READ,
    });
  };

  const unreadAnnotation = async (annotationIds, target) =>{
    const { ancestry } = target;
    const rootElementId = ancestry.slice(-1)[0].elementId;
    const READ = false;

    await authorAnnotationRead({
      rootElementId,
      annotationIds,
      elementType: target.type,
      elementId: target.id,
      read: READ,
    });
  };

  const deleteAnnotation = async (annotationId, target) => {
    const annotation = _findAnnotation(annotationId);
    if(IS_NEW_COMMENT in annotation) {
      _annotationsCache[annotation.motivation].deleteAnnotation(annotationId);
    } else {
      await authorAnnotationDelete({
        annotationId,
        elementId: target.id,
        elementType: target.type,
      });
      _annotationsCache[annotation.motivation].deleteAnnotation(annotationId);
    }
  };

  const invalidateAnnotationsInCache = (elementId) => {
    const annotationsFromCache = _annotationsCache[IDENTIFYING] && _annotationsCache[IDENTIFYING]._annotations;

    if (annotationsFromCache) {
      Object.keys(annotationsFromCache).forEach((annotationKey) => {
        const annotation = annotationsFromCache[annotationKey];
        if (annotation && annotation.elementId === elementId) {
          _annotationsCache[annotation.motivation].deleteAnnotation(annotation.id);
        }
      });
    }
  };

  const refetchAnnotations = async (target, motivation = IDENTIFYING) => {
    await _fetchAnnotationsByMotivation(motivation, target);
  };

  const getSubscribers = (eventName) => subscribers[eventName];

  const publishEvent = (eventName, payload, metadata = {}) => {
    if (!subscribers[eventName]) return;

    subscribers[eventName].forEach((callback) => callback(payload, metadata));
  };

  const subscribeToEvent = (eventName, callback) => {
    if (!subscribers[eventName]) {
      subscribers[eventName] = [];
    }
    subscribers[eventName].push(callback);
    // unsubscribe event
    return () => {
      const index = subscribers[eventName].indexOf(callback);
      subscribers[eventName].splice(index, 1);
    };
  };

  const createAnnotationTarget = (id, rootElementId) => ({ id,
    ancestry: [{ elementId: rootElementId }]});

  //todo: refactor to fetch from cache
  const getRootCommentIdsForUnreadReplies = async (rootElementId,elementType, lessonId) => {
    const repliesRootCommentIds = [];
    const annotationReplyList =  await authorAnnotationList({
      rootElementId : rootElementId,
      elementType: elementType,
      motivation: REPLYING,
      elementId: lessonId,
    });

    if(annotationReplyList.length > 0) {
      annotationReplyList.forEach((reply) => {
        if(!reply.read && !repliesRootCommentIds.includes(reply.targetJson[0] ?.subTarget?.commentId)) {
          repliesRootCommentIds.push(`${reply.targetJson[0]?.subTarget?.commentId}`);
        }
      });
    }

    return repliesRootCommentIds;
  };

  return {
    isRegistered: () => isRegistered,

    register: (connectionProvider) => {
      isRegistered = true;
      _annotationsCache = {};
      subscribers = {};
      annotationResolver = new AnnotationResolver(connectionProvider);
      accountResolver = new AccountResolver(connectionProvider);
    },
    getAnnotationsWithAccountList,
    authorAnnotationCreate,
    authorAnnotationGet,
    authorAnnotationList,
    authorAnnotationUpdate,
    authorAnnotationAggregate,
    authorAnnotationResolve,
    authorAnnotationRead,
    authorAnnotationDelete,
    authorAnnotationsDelete,
    authorAnnotationsRestore,
    getAnnotationsByMotivation,
    getAnnotationById,
    subscribeToAnnotationMotivation,
    createAnnotation,
    setAnnotationBody,
    resolveAnnotation,
    readAnnotation,
    unreadAnnotation,
    deleteAnnotation,
    invalidateAnnotationsInCache,
    notifyUpdate,
    notifyDelete,
    refetchAnnotations,
    getSubscribers,
    subscribeToEvent,
    createAnnotationTarget,
    getRootCommentIdsForUnreadReplies,
  };
})();
